import {
	GoogleMap,
	Marker,
	useGoogleMap,
	useJsApiLoader,
} from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { MapCards } from './mapCard.js';
import { InfoWindow } from './InfoWindow.js';
import './css/map-styles.css';

const mapContainerStyle = {
	position: 'relative',
	height: '100%',
	width: '100%',
	zIndex: 1,
};

const mapsAPIKey = localized_params.map_api_key;

const months = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];
const allEvents = [];
const states = [];
const stateOptions = [];
const stateEvents = [];
let bounds = null;
let mapRef = null;

let filteredEvents = [];

function WtrMap() {
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: mapsAPIKey,
	});

	const [map, setMap] = useState(null);
	const [mapEvents, setMapEvents] = useState(null);
	const [mapRepeatEvents, setMapRepeatEvents] = useState(null);
	const [activeMarker, setActiveMarker] = useState(null);
	const [stateList, setStateList] = useState(null);
	const [searchState, setSearchState] = useState(null);
	const [searchStartDate, setSearchStartDate] = useState(null);
	const [searchEndDate, setSearchEndDate] = useState(null);
	const [zipSearch, setZipSearch] = useState(null);
	const [zipData, setZipData] = useState(null);
	const [zipDistance, setZipDistance] = useState(5000);
	const [defaultCenter, setDefaultCenter] = useState({
		lat: 37.09024,
		lng: -95.712891,
	});

	let repeatEvents = [];

	useEffect(() => {
		// Don't filter if no filters are set
		if (
			searchStartDate === null
			&& searchEndDate === null
			&& searchState === null
			&& (zipData === null || zipDistance === null || zipDistance === 5000)
		) {
			resetMapView();
			updateMapEvents(allEvents);
			return;
		}

		// Filter events based on filter settings
		let loopEvents = allEvents;
		filteredEvents = [];

		if (searchStartDate !== null) {
			if (filteredEvents.length) {
				loopEvents = filteredEvents;
				filteredEvents = [];
			}

			loopEvents.forEach((event) => {
				if (event.startDate >= searchStartDate) {
					filteredEvents.push(event);
				}
			});
		}

		if (searchEndDate !== null) {
			if (filteredEvents.length) {
				loopEvents = filteredEvents;
				filteredEvents = [];
			}

			loopEvents.forEach((event) => {
				if (event.endDate <= searchEndDate) {
					filteredEvents.push(event);
				}
			});
		}

		if (searchState) {
			if (filteredEvents.length) {
				loopEvents = filteredEvents;
				filteredEvents = [];
			}

			bounds = new window.google.maps.LatLngBounds();
			loopEvents.forEach((event) => {
				if (event.state === searchState) {
					bounds.extend({
						lat: Number(event.latitude),
						lng: Number(event.longitude),
					});
					filteredEvents.push(event);
				}
			});
			mapRef.fitBounds(bounds);

		}

		if (zipData && zipDistance && zipDistance !== 5000) {
			if (filteredEvents.length) {
				loopEvents = filteredEvents;
				filteredEvents = [];
			}

			const earthRadius = 6378137;
			const latRadians =
				((parseInt(zipDistance) * 1609.34) / earthRadius) *
				(180 / Math.PI);
			const lngRadians =
				latRadians / Math.cos((Math.PI / 180) * parseFloat(zipData[3]));

			const north = parseFloat(zipData[3]) + latRadians;
			const south = parseFloat(zipData[3]) - latRadians;
			const east = parseFloat(zipData[4]) + lngRadians;
			const west = parseFloat(zipData[4]) - lngRadians;

			mapRef.fitBounds({ north, south, east, west });

			const centerLat = parseFloat(zipData[3]);
			const centerLng = parseFloat(zipData[4]);

			const toRad = (value) => (value * Math.PI) / 180;

			const lat1 = toRad(centerLat);
			const lng1 = toRad(centerLng);

			loopEvents.forEach((event) => {
				const lat2 = toRad(event.latitude);
				const lng2 = toRad(event.longitude);

				const dLat = lat2 - lat1;
				const dLng = lng2 - lng1;

				const a =
					Math.sin(dLat / 2) * Math.sin(dLat / 2) +
					Math.cos(lat1) *
					Math.cos(lat2) *
					Math.sin(dLng / 2) *
					Math.sin(dLng / 2);

				const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
				const distance = c * 3958.8;

				if (distance <= parseFloat(zipDistance)) {
					filteredEvents.push(event);
				}
			});
		}

		updateMapEvents(filteredEvents);
	}, [searchState, zipData, zipDistance, searchStartDate, searchEndDate]);

	const resetMapView = () => {
		if (mapRef !== null) {
			const southWest = { lat: 27.796308, lng: -124.5 };
			const northEast = { lat: 45.384358, lng: -66.93457 };
			bounds = new window.google.maps.LatLngBounds(southWest, northEast);
			mapRef.fitBounds(bounds);
			setTimeout(() => {
				mapRef.setZoom(mapRef.getDiv().offsetWidth > 650 ? 4 : 3);
			}, 100);
		}
	}

	const updateMapEvents = (events) => {

		events.sort((a, b) => {
			return a.street_address < b.street_address ? -1 : 1;
		});

		repeatEvents = [];
		let loopingEvents = [];

		events.forEach((event, index) => {
			// If we have events in loopingEvents and this event has a different address
			if (loopingEvents.length && event.street_address !== loopingEvents[0].street_address) {
				// If we had multiple events at the same address, add them to repeatEvents
				if (loopingEvents.length >= 2) {
					repeatEvents.push([...loopingEvents]);
				}
				// Start a new group
				loopingEvents = [];
			}

			// Add current event to the group
			loopingEvents.push(event);

			// Handle the last group if we're at the end of the array
			if (index === events.length - 1 && loopingEvents.length >= 2) {
				repeatEvents.push([...loopingEvents]);
			}
		});

		setMapEvents(events);
		setMapRepeatEvents(repeatEvents);
	}

	const getEvents = async () => {
		const response = await fetch(localized_params.resturl + 'wtr/v2/map-events');
		const events = await response.json();

		events.forEach((event) => {
			event.key = event.ID;

			event.earlyDate = new Date(event.early_start_date + ' 00:00:00');
			event.startDate = new Date(event.start_date + ' 00:00:00');
			event.endDate = new Date(event.end_date + ' 00:00:00');

			if (states.includes(event.state)) {
				stateEvents[event.state] += '|' + event.title;
			} else {
				states.push(event.state);
				stateEvents[event.state] = event.title;
			}

			allEvents.push(event);
		});

		states.sort();
		states.forEach((state) => {
			stateOptions.push(state);
		});
		setStateList(stateOptions);

		updateMapEvents(allEvents);
	}

	const handleDateSelectChange = (event) => {
		const dateObj = new Date(event.target.value + ' 00:00:00');

		if (event.target.id === 'wtrmap-start-date') {
			setSearchStartDate(dateObj);
		}
		if (event.target.id === 'wtrmap-end-date') {
			setSearchEndDate(dateObj);
		}
	};

	const handleZipChange = (event) => {
		setSearchState(null);

		setZipSearch(event.target.value);

		const obj = {
			action: 'zip_lookup',
			zip: event.target.value,
		}

		jQuery.post(localized_params.ajaxurl, obj, function (data) {
			setZipData(data.data);
		});
	};

	const handleDistanceChange = (event) => {
		setSearchState(null);
		setZipDistance(parseInt(event.target.value));
	};

	const handleStateChange = (state) => {
		setZipSearch(null);
		setZipData(null);
		setSearchState(state.target.value ? state.target.value : null);
	};

	const handleClearResults = () => {
		jQuery('#wtrmap-start-date').val('');
		jQuery('#wtrmap-end-date').val('');
		jQuery('#wtrmap-zip').val('');
		jQuery('#wtrmap-distance').val(5000);
		jQuery('#wtrmap-state').val('');

		setSearchState(null);
		setSearchStartDate(null);
		setSearchEndDate(null);
		setZipSearch(null);
		setZipData(null);
		setActiveMarker(null);

		resetMapView();
	};

	const onLoad = React.useCallback(async function callback(map) {
		mapRef = map;
		setMap(map);
		await getEvents();
		resetMapView();
	}, []);

	const onUnmount = React.useCallback(function callback() {
		setMap(null);
	}, []);

	return isLoaded ? (
		<>
			<div className={'wtrsearchbar'}>

				<div className={'wtrsearchbar-filter wtrsearchbar-filter-state'}>
					<div className={'wtrsearchbar-filter-label'}>Search by state</div>
					<label className={'wtrsearchbar-input-frame'}>
						<div className={'wtrsearchbar-input-label'}>State</div>
						<select id="wtrmap-state" className={'wtrmap-input'} defaultValue="" onChange={handleStateChange}>
							<option selected></option>
							{stateList &&
								stateList.map((value) => (
									<option key={value} value={value}>{value}</option>
								))}
						</select>
					</label>
				</div>

				<div className={'wtrsearchbar-filter wtrsearchbar-filter-zip'}>
					<div className={'wtrsearchbar-filter-label'}>Search by zip code</div>
					<label className={'wtrsearchbar-input-frame'}>
						<div className={'wtrsearchbar-input-label'}>Zip Code</div>
						<input type={'integer'} className={'wtrmap-input'} id={'wtrmap-zip'} onBlur={handleZipChange} />
					</label>
					<label className={'wtrsearchbar-input-frame'}>
						<div className={'wtrsearchbar-input-label'}>Distance</div>
						<select id={'wtrmap-distance'} className={'wtrmap-input'} onChange={handleDistanceChange}>
							<option value="5000"></option>
							<option value="25">25 miles</option>
							<option value="50">50 miles</option>
							<option value="100">100 miles</option>
							<option value="250">250 miles</option>
							<option value="500">500 miles</option>
						</select>
					</label>
				</div>

				<div className={'wtrsearchbar-filter wtrsearchbar-filter-date'}>
					<div className={'wtrsearchbar-filter-label'}>Search by date range</div>
					<label className={'wtrsearchbar-input-frame'}>
						<div className={'wtrsearchbar-input-label'}>From Date</div>
						<input type={'date'} id={'wtrmap-start-date'} className={'wtrmap-input wtrmap-date-picker'} onChange={handleDateSelectChange} />
					</label>
					<label className={'wtrsearchbar-input-frame'}>
						<div className={'wtrsearchbar-input-label'}>To Date</div>
						<input type={'date'} id={'wtrmap-end-date'} className={'wtrmap-input wtrmap-date-picker'} onChange={handleDateSelectChange} />
					</label>
				</div>

				<div className={'wtrsearchbar-clear'}>
					<button type="button" id={'wtrmap-filter-clear'} className={'wtrsearchbar-clear-button'} onClick={handleClearResults}>Clear Search</button>
				</div>

			</div>

			<div className={'wtrmap-google-map'}>
				<div
					style={{
						position: 'relative',
						top: '0px',
						width: '100%',
						maxWidth: '100%',
						height: '100%',
					}}
				>
					<GoogleMap
						mapContainerStyle={mapContainerStyle}
						center={defaultCenter}
						onLoad={onLoad}
						onUnmount={onUnmount}
						defaultZoom={4}
						zoom={4}
						options={{
							streetViewControl: false,
							mapTypeControl: false,
							fullscreenControl: false,
							maxZoom: 16,
							clickableIcons: false,
						}}
					>
						{mapEvents?.map((event) => {
							return (
								<Marker
									key={event.ID}
									position={{
										lat: Number(event.latitude),
										lng: Number(event.longitude),
									}}
									onClick={() => setActiveMarker(event.ID)}
								>
									{String(activeMarker).replace("*", "") === event.ID ? (
										<InfoWindow
											lat={event.latitude}
											lon={event.longitude}
											event={event}
											activeMarker={null}
											onShow={(marker) => setActiveMarker(marker)}
										/>
									) : null}
								</Marker>
							);
						})}
						{mapRepeatEvents?.map((events) => {
							return (
								<Marker
									key={events[0].id + 'M'}
									position={{
										lat: Number(events[0].latitude),
										lng: Number(events[0].longitude),
									}}
									onClick={() => setActiveMarker(events[0].ID + 'M')}
								>
									{String(activeMarker).replace("*", "") === events[0].ID + 'M' ? (
										<InfoWindow
											lat={events[0].latitude}
											lon={events[0].longitude}
											events={events}
											activeMarker={null}
											onShow={(marker) => setActiveMarker(marker)}
										/>
									) : null}
								</Marker>
							);
						})}
					</GoogleMap>
				</div>
			</div>

			<h3 className="wtrmap-results-title">Upcoming Getaways:</h3>

			<div className={'wtrmap-all-cards'}>
				<MapCards
					mapEvents={mapEvents}
					clickKeyHandler={setActiveMarker}
				/>
			</div>
		</>
	) : null;
}

export { useGoogleMap, months }
export { allEvents, states, stateEvents, bounds };
export default WtrMap;
